/**
 * @file hardwareLifecycleService.js
 * @description The /hardware workshop: registration (TCU without a machine),
 *              live commissioning checks, installation (mapping + evidence)
 *              and bench/pre-install test sessions.
 *              Server: controllers/hardwareLifecycleController.js.
 */
import apiClient from './apiClient';

const hardwareLifecycleService = {
    /** The commissioning ladder — poll while wiring (5s is comfortable). */
    async liveCheck(deviceId) {
        return apiClient.get(`/hardware/live-check/${encodeURIComponent(deviceId)}`);
    },

    /** Dropdown data: devices, unmapped devices, free machines, bench units. */
    async pickers() {
        return apiClient.get('/hardware/pickers');
    },

    /**
     * Per-model CAN frame checklist (machine_can_ids) vs the frames the TCU is
     * actually receiving — green tick / red cross per frame. Poll during the
     * installation's checking step.
     */
    async canCheck(deviceId, equipmentId) {
        return apiClient.get(`/hardware/can-check?device_id=${encodeURIComponent(deviceId)}&equipment_id=${encodeURIComponent(equipmentId)}`);
    },

    /** Registration — a new TCU, no machine required. */
    async register(payload) {
        return apiClient.post('/hardware/devices', payload);
    },

    /**
     * Correct an existing registration. Only the fields in `payload` are written.
     * The IMEI is fixed (it keys the unit's telemetry and sessions) and the
     * machine mapping belongs to Installation — neither can be changed here.
     */
    async updateDevice(deviceId, payload) {
        return apiClient.patch(`/hardware/devices/${encodeURIComponent(deviceId)}`, payload);
    },

    async startInstallation(deviceId, equipmentId) {
        return apiClient.post('/hardware/installations', { device_id: deviceId, equipment_id: equipmentId });
    },
    /** action: 'commission' | 'fail' (+ display meters, notes, force). */
    async finishInstallation(id, body) {
        return apiClient.patch(`/hardware/installations/${id}`, body);
    },
    async listInstallations(limit = 25) {
        return apiClient.get(`/hardware/installations?limit=${limit}`);
    },

    /**
     * Edit a finished installation: move a fitted TCU onto another machine, or
     * take it off one.
     * @param {string} deviceId
     * @param {string|null} equipmentId  the machine to move it to; null/'' unmaps it
     * @param {object} opts {notes, force} — `force` confirms displacing the
     *        device already on the target machine (the server refuses without it).
     */
    async updateDeviceMapping(deviceId, equipmentId, opts = {}) {
        return apiClient.patch(`/hardware/devices/${encodeURIComponent(deviceId)}/mapping`,
            { equipment_id: equipmentId || null, ...opts });
    },

    /**
     * Open a test session.
     * @param {object} body {identifier, test_type: INSTALL|DEVICE|MACHINE,
     *                       scopes: ['device','machine'], save}
     * The server resolves the identifier (device id / machine name) to the TCU
     * and answers with device_id + equipment_id. With `save: false` nothing is
     * recorded and `id` comes back null — the run is watch-only.
     */
    async startTest(body) {
        return apiClient.post('/hardware/tests', body);
    },
    /** action: 'pass' | 'fail' | 'abandon'. A saved session files its data here. */
    async finishTest(id, action, notes) {
        return apiClient.patch(`/hardware/tests/${id}`, { action, notes });
    },
    async listTests(limit = 25) {
        return apiClient.get(`/hardware/tests?limit=${limit}`);
    },
    /** One saved session, with the data it captured while it ran. */
    async getTest(id) {
        return apiClient.get(`/hardware/tests/${id}`);
    },
};

export default hardwareLifecycleService;
